/* eslint-disable */
import React, { FC, ReactElement, useState, useRef, useEffect, useMemo } from 'react';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import GoogleMapReact, { Coords } from 'google-map-react';
import { useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import { IconButton, Theme } from '@mui/material';
// Components
import SvgIcon from '../icons/SvgIcon';
import CarPinComponent from './carPinComponent';
import PickUpPinComponent from './pickUpPinComponent';
import DropOffPinComponent from './dropOffPinComponent';
// Modules
import { Stop, StopType, Location, Ride, Status } from '../../core/apiTypes';
// Resources
import { fitBounds, getBounds, getCustomMap, mapUtils, setCenter } from '../../utils/map';
import {
    findStopAddress,
    findStopLocation,
    getFinishedAndCancelledStatus,
    getRideTrackingShowDO,
    getRideTrackingZoom,
    getRideTrackingZoomPU,
    getSOSStatus,
    getStartPinStatus,
} from '../../utils/business';
import envConfig from '../../config/currentEnv';
import colors from '../../resources/style/colors';
import { IconAxel } from '../../resources/images';
import { getBottomBarHeight, getTopBarHeight, getUser } from '../../localStorageAPI';
import { useGeolocation } from '../hook/useLocation';
import { paths } from '../../navigation/routes/paths';
import PositionPinComponent from './positionPinComponent';
import { MAP_MAX_ZOOM, MAP_MAX_ZOOM_FIT_BOUNDS } from '../../utils/constants';
import { useTheme } from '../../context/ThemeContext';

interface StyleProps {
    statusBarHeight: number;
    bottomBarHeight: number;
}

interface MapViewProps {
    children?: React.ReactNode;
    onApiLoaded?: (map, maps) => void;
    center?: Coords;
    zoom?: number;
    stops: Stop[];
    reservation: Ride;
    waypoints: Location[];
    actionsCustom?: GoogleMapReact.MapOptions;
    actionsDisabled?: boolean;
    roundedTopBorder?: boolean;
    refreshBounds?: boolean; // re apply bounds, zoom, center at every render
    className?: string;
    style?: any;
    backArrow?: boolean;
    /** Where the back arrow goes. Defaults to popping the history stack. */
    onBack?: () => void;
    showCurrentPositionPin?: boolean;
    /** Map filling the screen, as opposed to a card in a list: its controls must clear the system bars. */
    fullScreen?: boolean;
}

const MapView: FC<MapViewProps> = (props): ReactElement => {
    const classes = useStyles({
        statusBarHeight: getTopBarHeight() || 0,
        bottomBarHeight: props.fullScreen ? getBottomBarHeight() : 0,
    });
    const {
        children,
        center = mapUtils.defaultCenter,
        zoom = mapUtils.defaultZoom,
        onApiLoaded,
        stops,
        reservation,
        waypoints,
        actionsDisabled = false,
        actionsCustom = {},
        roundedTopBorder = false,
        refreshBounds = true,
        backArrow = false,
        onBack,
        showCurrentPositionPin = false,
        className,
        style,
    } = props;

    const [open, setOpen] = useState(true);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const [googleMap, setMap] = useState(null);
    const [shouldReload, setShouldReload] = useState<boolean>(true);
    const [driverPosition, setDriverPosition] = useState<Location | null>(
        reservation?.driver_tracking?.location || null,
    );

    const { mode } = useTheme();
    const rideTrack = useRef(null);
    const currentPosition = useGeolocation();
    const navigate = useNavigate();
    const user = getUser();
    const passengerIsConnectedUser = reservation?.riders?.[0].id === user?.id;

    const currentBookingPickup: Location = findStopLocation(stops, StopType.PICKUP);
    const currentBookingDropOff: Location = findStopLocation(stops, StopType.DROP_OFF);

    const geolocAllowed = !(currentPosition?.error?.code === 1);
    const showDo = getRideTrackingShowDO().includes(reservation?.status);
    const isHomeTrackingMobile = document.location.pathname.startsWith(paths.homeTrackingMobile.replace(':id', ''));

    if (googleMap?.map && googleMap?.maps) {
        if (shouldReload) {
            const coordinates = [];
            if (currentBookingPickup) {
                coordinates.push(currentBookingPickup);
            }
            if (showDo && currentBookingDropOff) {
                coordinates.push(currentBookingDropOff);
            }
            if (getRideTrackingZoom().includes(reservation?.status) && reservation?.driver_tracking?.location) {
                coordinates.push(reservation.driver_tracking.location);
            }

            if (showDo && waypoints) {
                coordinates.push(...waypoints);
            }

            const bounds = getBounds(coordinates, googleMap.maps);
            if (coordinates.length === 1) {
                setCenter(googleMap.map, coordinates[0]);
                googleMap.map.setZoom(zoom);
            } else if (coordinates.length >= 2) {
                fitBounds(googleMap.map, bounds);
                if (googleMap.map.getZoom() > MAP_MAX_ZOOM_FIT_BOUNDS) {
                    googleMap.map.setZoom(MAP_MAX_ZOOM_FIT_BOUNDS);
                }
            } else {
                setCenter(googleMap.map, center ?? mapUtils.defaultCenter);
                googleMap.map.setZoom(zoom);
            }
            if (!refreshBounds) {
                setShouldReload(false);
            }
        }
    }

    useEffect(() => {
        console.log('Reservation Status changed to', reservation.status);
        setShouldReload(true);
        if (googleMap?.map && googleMap?.maps) {
            if (reservation.status === Status.FINISHED && currentBookingPickup) {
                setCenter(googleMap.map, currentBookingPickup); // Center on PU
            } else if (reservation.status === Status.AT_PICKUP && reservation?.driver_tracking?.location) {
                setCenter(googleMap.map, reservation?.driver_tracking?.location); // Center on driver position
            } else if (reservation.status === Status.PASSENGER_ON_BOARD && reservation?.driver_tracking?.location) {
                setCenter(googleMap.map, reservation?.driver_tracking?.location); // Center on driver position
            }
            googleMap.map.setZoom(zoom);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [googleMap, reservation.status]);

    useEffect(() => {
        if (
            googleMap?.map &&
            googleMap?.maps &&
            stops.length > 1 &&
            waypoints?.length > 0 &&
            reservation?.status !== Status.AT_PICKUP
        ) {
            if (rideTrack.current) {
                rideTrack.current.setMap(null);
            }
            rideTrack.current = new googleMap.maps.Polyline({
                path: waypoints,
                strokeColor: colors.blue,
                strokeOpacity: 1.0,
                strokeWeight: 4,
            });
            rideTrack.current.setMap(googleMap?.map);
        }
        return () => {
            if (rideTrack.current) {
                rideTrack.current.setMap(null);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [googleMap, waypoints]);

    useEffect(() => {
        if (reservation?.driver_tracking?.location) {
            const newLocation = reservation.driver_tracking.location;

            if (driverPosition) {
                // Animate the transition to the new location
                const seconds = 5; // Animation duration in seconds
                const duration = seconds * 1000; // Animation duration in milliseconds
                const steps = seconds * 60; // Number of animation steps
                const { lat: startLat, lng: startLng } = driverPosition;
                const { lat: endLat, lng: endLng } = newLocation;

                let step = 0;
                const interval = setInterval(() => {
                    step++;
                    const lat = startLat + ((endLat - startLat) * step) / steps;
                    const lng = startLng + ((endLng - startLng) * step) / steps;
                    setDriverPosition({ lat, lng });

                    if (step === steps) {
                        clearInterval(interval);
                    }
                }, duration / steps);
            } else {
                // If no previous position, set the new location directly
                setDriverPosition(newLocation);
            }
        }
    }, [reservation?.driver_tracking?.location]);

    const onGoogleReady = (map, maps) => {
        setMap({ map, maps });
        if (onApiLoaded) {
            onApiLoaded(map, maps);
        }
    };

    const goToGeoloc = () => {
        let coords =
            passengerIsConnectedUser && geolocAllowed
                ? currentPosition?.latLng || reservation?.driver_tracking?.location
                : reservation?.driver_tracking?.location;

        if (getRideTrackingZoomPU().includes(reservation?.status) && (!geolocAllowed || !passengerIsConnectedUser)) {
            coords = currentBookingPickup;
        }

        if (googleMap?.map && googleMap?.maps && coords) {
            setCenter(googleMap.map, coords);
            googleMap.map.setZoom(zoom * 1.25);
        }
    };

    const goBack = () => {
        if (onBack) {
            onBack();
            return;
        }
        navigate(-1);
    };

    //=========================================//
    //=============== SpeedDial ===============//
    //=========================================//

    const goToSOSPage = () => {
        navigate(paths.sos, {
            state: { id: reservation?.id, sosActivated: reservation?.emergency?.enabled },
        });
        handleClose();
    };

    const goToRideDetails = () => {
        navigate(paths.reservationsDetail.replace(':id', reservation?.id));
        handleClose();
    };

    // SpeedDial Floating Button - Disabled
    // const actions = useMemo(
    //     () => [
    //         {
    //             icon: (
    //                 <SvgIcon
    //                     icon={IconCommon.InformationOutline}
    //                     width={18}
    //                     height={18}
    //                     color="lightBlue"
    //                     fill="transparent"
    //                 />
    //             ),
    //             name: stringsRes.booking.moreDetails,
    //             onClick: goToRideDetails,
    //         },
    //         {
    //             icon: (
    //                 <SvgIcon
    //                     icon={IconCommon.SosOutline}
    //                     width={18}
    //                     height={18}
    //                     color="lightBlue"
    //                     fill="transparent"
    //                     stroke="transparent"
    //                 />
    //             ),
    //             color: 'red',
    //             name: stringsRes.sos.action,
    //             onClick: goToSOSPage,
    //         },
    //         // {
    //         //     icon: (
    //         //         <SvgIcon
    //         //             icon={IconCommon.Share}
    //         //             width={18}
    //         //             height={18}
    //         //             color="lightBlue"
    //         //             fill="transparent"
    //         //             stroke="transparent"
    //         //         />
    //         //     ),
    //         //     name: stringsRes.booking.shareRide,
    //         //     onClick: handleRideShare,
    //         // },
    //     ],
    //     [],
    // );

    return (
        <div className={clsx(classes.map, roundedTopBorder && classes.topBorderRounded, className)} style={style}>
            <GoogleMapReact
                bootstrapURLKeys={{ key: envConfig.currentEnv.googleMapKey }}
                yesIWantToUseGoogleMapApiInternals
                onGoogleApiLoaded={({ map, maps }) => onGoogleReady(map, maps)}
                // onChange={({ zoom }) => {
                //     console.log('DEBUG - Zoom level changed to:', zoom);
                // }}
                center={center}
                zoom={zoom}
                options={{
                    ...actionsCustom,
                    ...(actionsDisabled && { fullscreenControl: false, zoomControl: false, gestureHandling: 'none' }),
                    styles: getCustomMap(mode),
                    maxZoom: MAP_MAX_ZOOM,
                }}
            >
                {!getStartPinStatus().includes(reservation?.status) && driverPosition && (
                    <CarPinComponent
                        lat={driverPosition.lat}
                        lng={driverPosition.lng}
                        address=""
                        isAsap={false}
                        heading={reservation.driver_tracking?.heading}
                    />
                )}
                {currentBookingPickup?.lat && googleMap && (
                    <PickUpPinComponent
                        lat={currentBookingPickup.lat}
                        lng={currentBookingPickup.lng}
                        address={findStopAddress(stops, StopType.PICKUP)}
                        isAsap={false}
                    />
                )}
                {currentBookingDropOff?.lat && googleMap && (
                    <DropOffPinComponent
                        lat={currentBookingDropOff.lat}
                        lng={currentBookingDropOff.lng}
                        address={findStopAddress(stops, StopType.DROP_OFF)}
                        isAsap={false}
                    />
                )}
                {showCurrentPositionPin && currentPosition?.latLng && (
                    <PositionPinComponent {...currentPosition.latLng} />
                )}
                {children}
            </GoogleMapReact>
            {/* SpeedDial Floating Button - Disabled
            {isHomeTrackingMobile && getSOSStatus().includes(reservation?.status) && (
                <div className={classes.moreButtonContainer}>
                    <SpeedDial
                        ariaLabel="SpeedDial actions"
                        icon={
                            <SvgIcon
                                icon={IconCommon.InformationOutline}
                                fix={18}
                                color="lightBlue"
                                fill="transparent"
                            />
                        }
                        onClose={handleClose}
                        onOpen={handleOpen}
                        open={open}
                    >
                        {actions.map(({ name, icon, color, onClick }) => (
                            <SpeedDialAction
                                key={name}
                                icon={icon}
                                tooltipTitle={name}
                                tooltipOpen
                                tooltipPlacement="right"
                                classes={{ staticTooltip: classes[color] }}
                                onClick={onClick}
                            />
                        ))}
                    </SpeedDial>
                </div>
            )} */}
            {!getFinishedAndCancelledStatus().includes(reservation?.status) &&
                (passengerIsConnectedUser || (!passengerIsConnectedUser && reservation?.driver_tracking?.location)) && (
                    <div className={clsx(classes.geolocButtonContainer, 'geolocButtonContainer')}>
                        <IconButton onClick={goToGeoloc} className={classes.geolocButton} type="button">
                            <SvgIcon icon={IconAxel.Geolocalisation} width={18} height={18} stroke="currentColor" />
                        </IconButton>
                    </div>
                )}
            {backArrow && (
                <div className={classes.backButtonContainer}>
                    <IconButton onClick={goBack} className={classes.geolocButton} type="button">
                        <SvgIcon icon={IconAxel.ArrowLeft} width={18} height={18} stroke="currentColor" />
                    </IconButton>
                </div>
            )}
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        map: {
            flex: 1,
            display: 'flex',
            width: '100%',
            height: 200,
            maxHeight: 200,
            minHeight: 200,
            position: 'relative',
            // The fullscreen control is drawn by the Maps API, not by us: it is the only one left on
            // (every other map passes `fullscreenControl: false`) and it ships a white button with a
            // dark glyph, which stays light over the dark map. Google writes the background inline on
            // the button, hence `!important`; the glyph is an <img> whose data-URI src it swaps on
            // hover, so it is inverted rather than recoloured.
            ...(theme.palette.mode === 'dark' && {
                '& .gm-fullscreen-control': {
                    backgroundColor: `${theme.palette.colors.background.appBar}!important`,
                    '& > *': {
                        filter: 'invert(1)',
                    },
                },
            }),
        },
        topBorderRounded: {
            '& div': {
                borderRadius: '10px 10px 0px 0px',
            },
        },
        geolocButtonContainer: {
            position: 'absolute',
            // Stacked above the Android bottom system bar, which the edge-to-edge WebView draws
            // under — without it the button is clipped by the gesture handle.
            bottom: (props: StyleProps) => 22 + props.bottomBarHeight,
            right: 9, // Adjust the distance from the left as needed
            '@media (max-width: 200px)': {
                display: 'none !important',
            },
        },
        geolocButton: {
            zIndex: 1,
            background: theme.palette.colors.background.appBar,
            color: theme.palette.colors.fixed.darkWhite,
            '&:hover': {
                backgroundColor: theme.palette.colors.background.appBar,
            },
            '-webkit-box-shadow': '0px 4px 8px 0px #2F313F1A',
            '-moz-box-shadow': '0px 4px 8px 0px #2F313F1A',
            boxShadow: '0px 4px 8px 0px #2F313F1A',
        },
        backButtonContainer: {
            position: 'absolute',
            top: (props: StyleProps) => `${props.statusBarHeight + 9}px`,
            left: 9,
        },
        moreButtonContainer: {
            position: 'absolute',
            bottom: '22px', // Adjust the distance from the bottom as needed
            left: '9px', // Adjust the distance from the left as needed
            '@media (max-width: 200px)': {
                display: 'none !important',
            },
            '& .MuiFab-circular': {
                width: 42,
                height: 42,
                boxShadow: 'unset',
                backgroundColor: 'white',
                marginTop: 4,
                marginBottom: 4,
            },
            '& #SpeedDialactions-actions': {
                paddingBottom: 32,
                color: theme.palette.secondary.light,
            },
            '& .MuiSpeedDialAction-staticTooltipLabel': {
                left: '57%',
                height: 42,
                width: 180,
                backgroundColor: 'white',
                marginLeft: 0,
                display: 'flex',
                alignItems: 'center',
                boxShadow: 'unset',
                borderRadius: '0px 50px 50px 0px',
                fontFamily: 'Roboto',
                fontSize: 14,
                fontWeight: 500,
                color: 'inherit',
            },
        },
        red: {
            color: theme.palette.colors.text.error,
        },
        blue: {
            color: 'inherit',
        },
    }),
);

export default MapView;
